/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {Injectable, Renderer2, NgZone} from '@angular/core';
import {Observable, Observer} from 'rxjs';
import {LoggerService} from '../../../modules/shared/services/logger.service';
import {PointerEventCoordinate} from './interfaces';
import {share, finalize} from 'rxjs/operators';

@Injectable()
export class ResizableListenerService {
    public pointerDown: Observable<PointerEventCoordinate>;
    public pointerMove: Observable<PointerEventCoordinate>;
    public pointerUp: Observable<PointerEventCoordinate>;

    constructor(
        private renderer: Renderer2,
        private ngZone: NgZone,
        private logger: LoggerService,
    ) {
        this.logger.warn('ResizableListenerService constructor');
        this.pointerDown = new Observable((observer: Observer<PointerEventCoordinate>) => {
            let unsubscribeMouseDown: () => void;
            let unsubscribeTouchStart: () => void;

            ngZone.runOutsideAngular(() => {
                unsubscribeMouseDown = renderer.listen('document', 'mousedown',
                    (event: MouseEvent) => {
                        observer.next({
                            clientX: event.clientX,
                            clientY: event.clientY,
                            event
                        });
                    });

                unsubscribeTouchStart = renderer.listen('document', 'touchstart', (event: TouchEvent) => {
                    observer.next({clientX: event.touches[0].clientX, clientY: event.touches[0].clientY, event});
                });
            });

            return () => {
                unsubscribeMouseDown();
                unsubscribeTouchStart();
            };
        }).pipe(
            share(),
        );

        this.pointerMove = new Observable((observer: Observer<PointerEventCoordinate>) => {
            let unsubscribeMouseMove: () => void;
            let unsubscribeTouchMove: () => void;

            ngZone.runOutsideAngular(() => {
                unsubscribeMouseMove = renderer.listen('document', 'mousemove',
                    (event: MouseEvent) => {
                        observer.next({
                            clientX: event.clientX,
                            clientY: event.clientY,
                            event,
                        });
                    });

                unsubscribeTouchMove = renderer.listen('document', 'touchmove',
                    (event: TouchEvent) => {
                        observer.next({
                            clientX: event.targetTouches[0].clientX,
                            clientY: event.targetTouches[0].clientY,
                            event,
                        });
                    });
            });

            return () => {
                unsubscribeMouseMove();
                unsubscribeTouchMove();
            };
        }).pipe(
            share(),
        );

        this.pointerUp = new Observable((observer: Observer<PointerEventCoordinate>) => {
            let unsubscribeMouseUp: () => void;
            let unsubscribeTouchEnd: () => void;
            let unsubscribeTouchCancel: () => void;

            ngZone.runOutsideAngular(() => {
                unsubscribeMouseUp = renderer.listen(
                    'document',
                    'mouseup',
                    (event: MouseEvent) => {
                        observer.next({
                            clientX: event.clientX,
                            clientY: event.clientY,
                            event,
                        });
                    });

                unsubscribeTouchEnd = renderer.listen(
                    'document',
                    'touchend',
                    (event: TouchEvent) => {
                        observer.next({
                            clientX: event.changedTouches[0].clientX,
                            clientY: event.changedTouches[0].clientY,
                            event,
                        });
                    });

                unsubscribeTouchCancel = renderer.listen(
                    'document',
                    'touchcancel',
                    (event: TouchEvent) => {
                        observer.next({
                            clientX: event.changedTouches[0].clientX,
                            clientY: event.changedTouches[0].clientY,
                            event,
                        });
                    });
            });

            return () => {
                unsubscribeMouseUp();
                unsubscribeTouchEnd();
                unsubscribeTouchCancel();
            };
        }).pipe(
            share(),
        );
    }
}
